import React, { useEffect, useId, useState } from "react";
import { api, type PublicConfig } from "./api";

/**
 * "Countersign with another person's email and PIN" (test environment only;
 * owner, 2026-10-23). One tester works the test site at a time, so the giver
 * a dose names, or the licensed user a Med Staff visit waits for, types their
 * own email and PIN here, as the controlled-dose second person does. The
 * server registers its routes in the test environment alone
 * (server/test-env/countersign-as.ts) and keeps the real rules: a dose needs
 * its named giver, a visit a licensed user other than its recorder.
 */

let testEnv: Promise<boolean> | null = null;

/** True in the test environment (public-config), asked once per page load. */
export function useTestEnvironment(): boolean {
  const [isTest, setIsTest] = useState(false);
  useEffect(() => {
    testEnv ??= api<PublicConfig>("GET", "/api/public-config").then((r) => r.ok && r.data.environment === "test");
    let live = true;
    void testEnv.then((t) => live && setIsTest(t));
    return () => {
      live = false;
    };
  }, []);
  return isTest;
}

export function CountersignAs({ path, body = {}, onDone }: { path: string; body?: Record<string, unknown>; onDone: () => void }): React.ReactElement {
  const id = useId();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function sign() {
    setError(null);
    if (!email.trim() || pin.length !== 4) return setError("The second person enters their own email and 4-digit PIN.");
    setBusy(true);
    const r = await api<unknown>("POST", path, { ...body, email: email.trim(), pin });
    setBusy(false);
    setPin("");
    if (!r.ok) return setError((r.data as { error?: string }).error ?? "Not countersigned.");
    onDone();
  }
  return (
    <details className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
      <summary className="cursor-pointer font-medium text-amber-950">Countersign with another person's email and PIN</summary>
      <p className="mt-1 text-xs text-amber-950">Test site only — in the real app the other person signs in.</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div><label htmlFor={`${id}-e`} className="field-label">Second person's email</label><input id={`${id}-e`} type="email" autoComplete="off" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div><label htmlFor={`${id}-p`} className="field-label">Their PIN</label><input id={`${id}-p`} type="password" inputMode="numeric" autoComplete="off" maxLength={4} className="field-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} /></div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void sign()}>{busy ? "Countersigning…" : "Countersign as them"}</button>
    </details>
  );
}
